export type T_MarkdownTableAlignment = "center" | "left" | "right" | null;

export type T_ParsedMarkdownTable = {
  alignments: T_MarkdownTableAlignment[];
  headerCells: string[];
  rows: string[][];
};

export type T_MarkdownSegment =
  | { kind: "text"; markdown: string }
  | {
      complete: boolean;
      expression: string;
      kind: "math";
    }
  | {
      complete: boolean;
      kind: "table";
      table: T_ParsedMarkdownTable;
    };

type T_SourceLine = {
  content: string;
  raw: string;
};

type T_FenceState = {
  length: number;
  marker: "`" | "~";
};

type T_ParsedDelimiter = {
  alignments: T_MarkdownTableAlignment[];
  columnCount: number;
};

type T_MathDelimiter = "bracket" | "dollar";

type T_ParsedMathBlock = {
  complete: boolean;
  delimiter: T_MathDelimiter;
  expression: string;
  nextLineIndex: number;
};

/**
 * Splits GFM tables and display math out of assistant markdown before it
 * reaches the native markdown renderer. Ambiguous pipe-delimited stream tails
 * and live open $$ blocks are intentionally kept away from the renderer until
 * later content resolves them.
 */
export function segmentMarkdownTables(
  markdown: string,
  options?: { streamEnded?: boolean },
): T_MarkdownSegment[] {
  // Withholding ambiguous pipe tails only makes sense while the stream can
  // still resolve them. On a FINISHED message a trailing "a | b" line is
  // just text — withholding it would silently delete it forever. Default
  // to ended (safe for every non-streaming caller); only live parts opt in.
  const streamEnded = options?.streamEnded ?? true;
  const lines = splitSourceLines(markdown);
  const segments: T_MarkdownSegment[] = [];
  let textBuffer = "";
  let fence: T_FenceState | null = null;
  let heldAmbiguousTail = false;
  let lineIndex = 0;

  const flushText = () => {
    if (textBuffer.length === 0) return;
    segments.push({ kind: "text", markdown: textBuffer });
    textBuffer = "";
  };

  while (lineIndex < lines.length) {
    const line = lines[lineIndex];
    if (!line) break;

    if (fence) {
      textBuffer += line.raw;
      if (isClosingFence(line.content, fence)) fence = null;
      lineIndex += 1;
      continue;
    }

    const openingFence = parseOpeningFence(line.content);
    if (openingFence) {
      textBuffer += line.raw;
      fence = openingFence;
      lineIndex += 1;
      continue;
    }

    const mathBlock = parseMathBlock(lines, lineIndex);
    if (mathBlock) {
      const canRenderIncomplete =
        mathBlock.delimiter === "dollar" && !streamEnded;
      if (!mathBlock.complete && !canRenderIncomplete) {
        while (lineIndex < mathBlock.nextLineIndex) {
          const rawMathLine = lines[lineIndex];
          if (!rawMathLine) break;
          textBuffer += rawMathLine.raw;
          lineIndex += 1;
        }
        continue;
      }

      flushText();
      segments.push({
        complete: mathBlock.complete,
        expression: mathBlock.expression,
        kind: "math",
      });
      lineIndex = mathBlock.nextLineIndex;
      continue;
    }

    const delimiter = parseTableStart(lines, lineIndex);
    if (delimiter) {
      flushText();
      const headerCells = normalizeCells(
        parseTableCells(stripBlockquotePrefix(line.content)),
        delimiter.columnCount,
      ).map(cleanCellText);
      const rows: string[][] = [];
      let bodyLineIndex = lineIndex + 2;

      while (bodyLineIndex < lines.length) {
        const bodyLine = lines[bodyLineIndex];
        if (!bodyLine) break;
        const bodyContent = stripBlockquotePrefix(bodyLine.content);
        if (!containsUnescapedPipe(bodyContent)) break;
        if (parseTableStart(lines, bodyLineIndex)) break;

        rows.push(
          normalizeCells(
            parseTableCells(bodyContent),
            delimiter.columnCount,
          ).map(cleanCellText),
        );
        bodyLineIndex += 1;
      }

      segments.push({
        complete: streamEnded || bodyLineIndex < lines.length,
        kind: "table",
        table: {
          alignments: delimiter.alignments,
          headerCells,
          rows,
        },
      });
      lineIndex = bodyLineIndex;
      continue;
    }

    const content = stripBlockquotePrefix(line.content);
    const nextLine = lines[lineIndex + 1];
    const isLastLine = lineIndex === lines.length - 1;
    const hasAmbiguousHeader = containsUnescapedPipe(content);
    const hasPartialDelimiterTail =
      hasAmbiguousHeader &&
      lineIndex === lines.length - 2 &&
      nextLine !== undefined &&
      isPotentialPartialDelimiter(nextLine.content);

    if (
      !streamEnded &&
      hasAmbiguousHeader &&
      (isLastLine || hasPartialDelimiterTail)
    ) {
      heldAmbiguousTail = true;
      break;
    }

    textBuffer += line.raw;
    lineIndex += 1;
  }

  flushText();

  if (segments.length === 0 && !heldAmbiguousTail) {
    return [{ kind: "text", markdown }];
  }
  return segments;
}

function parseMathBlock(
  lines: T_SourceLine[],
  openingLineIndex: number,
): T_ParsedMathBlock | null {
  const openingLine = lines[openingLineIndex];
  if (!openingLine) return null;
  const openingContent = stripMathIndent(
    stripBlockquotePrefix(openingLine.content),
  );

  if (openingContent.startsWith("$$")) {
    return parseDelimitedMathBlock({
      closingDelimiter: "$$",
      delimiter: "dollar",
      lines,
      openingContent,
      openingDelimiter: "$$",
      openingLineIndex,
    });
  }

  if (openingContent.startsWith("\\[")) {
    return parseDelimitedMathBlock({
      closingDelimiter: "\\]",
      delimiter: "bracket",
      lines,
      openingContent,
      openingDelimiter: "\\[",
      openingLineIndex,
    });
  }

  return null;
}

function parseDelimitedMathBlock({
  closingDelimiter,
  delimiter,
  lines,
  openingContent,
  openingDelimiter,
  openingLineIndex,
}: {
  closingDelimiter: "$$" | "\\]";
  delimiter: T_MathDelimiter;
  lines: T_SourceLine[];
  openingContent: string;
  openingDelimiter: "$$" | "\\[";
  openingLineIndex: number;
}): T_ParsedMathBlock | null {
  const openingRemainder = openingContent.slice(openingDelimiter.length);
  const sameLineClosingIndex = findTerminalDelimiter(
    openingRemainder,
    closingDelimiter,
  );
  if (sameLineClosingIndex >= 0) {
    return {
      complete: true,
      delimiter,
      expression: openingRemainder.slice(0, sameLineClosingIndex),
      nextLineIndex: openingLineIndex + 1,
    };
  }

  // A closing marker followed by prose is inline markdown, not a display
  // block. Leaving the whole line as text keeps constructs such as
  // "$$x$$ per share" out of the native block-math path.
  if (openingRemainder.includes(closingDelimiter)) return null;

  const expressionLines: string[] = [];
  if (openingRemainder.trim().length > 0) {
    expressionLines.push(openingRemainder);
  }

  let lineIndex = openingLineIndex + 1;
  while (lineIndex < lines.length) {
    const line = lines[lineIndex];
    if (!line) break;
    const content = stripBlockquotePrefix(line.content);
    const closingContent = stripMathIndent(content);
    const closingIndex = findTerminalDelimiter(
      closingContent,
      closingDelimiter,
    );

    if (closingIndex >= 0) {
      const finalExpressionLine = closingContent.slice(0, closingIndex);
      if (finalExpressionLine.length > 0) {
        expressionLines.push(finalExpressionLine);
      }
      return {
        complete: true,
        delimiter,
        expression: expressionLines.join("\n"),
        nextLineIndex: lineIndex + 1,
      };
    }

    expressionLines.push(content);
    lineIndex += 1;
  }

  return {
    complete: false,
    delimiter,
    expression: expressionLines.join("\n"),
    nextLineIndex: lines.length,
  };
}

function findTerminalDelimiter(
  content: string,
  delimiter: "$$" | "\\]",
): number {
  const delimiterIndex = content.lastIndexOf(delimiter);
  if (delimiterIndex < 0) return -1;
  return content.slice(delimiterIndex + delimiter.length).trim().length === 0
    ? delimiterIndex
    : -1;
}

function stripMathIndent(line: string): string {
  return line.replace(/^ {0,3}/, "");
}

function splitSourceLines(markdown: string): T_SourceLine[] {
  const lines: T_SourceLine[] = [];
  let start = 0;

  while (start < markdown.length) {
    const newlineIndex = markdown.indexOf("\n", start);
    const end = newlineIndex === -1 ? markdown.length : newlineIndex + 1;
    const raw = markdown.slice(start, end);
    let content = newlineIndex === -1 ? raw : raw.slice(0, -1);
    if (content.endsWith("\r")) content = content.slice(0, -1);
    lines.push({ content, raw });
    start = end;
  }

  return lines;
}

function parseTableStart(
  lines: T_SourceLine[],
  headerLineIndex: number,
): T_ParsedDelimiter | null {
  const headerLine = lines[headerLineIndex];
  const delimiterLine = lines[headerLineIndex + 1];
  if (!headerLine || !delimiterLine) return null;

  const headerContent = stripBlockquotePrefix(headerLine.content);
  if (!containsUnescapedPipe(headerContent)) return null;

  const delimiter = parseDelimiterLine(
    stripBlockquotePrefix(delimiterLine.content),
  );
  if (!delimiter) return null;
  if (parseTableCells(headerContent).length !== delimiter.columnCount) {
    return null;
  }
  return delimiter;
}

function parseDelimiterLine(line: string): T_ParsedDelimiter | null {
  if (!containsUnescapedPipe(line)) return null;
  const cells = parseTableCells(line);
  if (cells.length === 0) return null;

  const alignments: T_MarkdownTableAlignment[] = [];
  for (const cell of cells) {
    const marker = cell.trim();
    if (!/^:?-+:?$/.test(marker)) return null;

    const leftAligned = marker.startsWith(":");
    const rightAligned = marker.endsWith(":");
    if (leftAligned && rightAligned) {
      alignments.push("center");
    } else if (leftAligned) {
      alignments.push("left");
    } else if (rightAligned) {
      alignments.push("right");
    } else {
      alignments.push(null);
    }
  }

  return { alignments, columnCount: cells.length };
}

function parseTableCells(line: string): string[] {
  const trimmed = line.trim();
  const cells: string[] = [];
  let cell = "";

  for (let index = 0; index < trimmed.length; index += 1) {
    const character = trimmed[index];
    const nextCharacter = trimmed[index + 1];
    if (character === "\\" && nextCharacter === "|") {
      cell += "|";
      index += 1;
      continue;
    }
    if (character === "|") {
      cells.push(cell.trim());
      cell = "";
      continue;
    }
    cell += character ?? "";
  }
  cells.push(cell.trim());

  if (trimmed.startsWith("|") && cells[0] === "") cells.shift();
  if (endsWithUnescapedPipe(trimmed) && cells.at(-1) === "") cells.pop();
  return cells;
}

function normalizeCells(cells: string[], columnCount: number): string[] {
  return Array.from({ length: columnCount }, (_, index) => cells[index] ?? "");
}

function cleanCellText(cell: string): string {
  return cell
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .trim();
}

function containsUnescapedPipe(line: string): boolean {
  for (let index = 0; index < line.length; index += 1) {
    if (line[index] !== "|") continue;
    let precedingBackslashes = 0;
    for (
      let backslashIndex = index - 1;
      backslashIndex >= 0 && line[backslashIndex] === "\\";
      backslashIndex -= 1
    ) {
      precedingBackslashes += 1;
    }
    if (precedingBackslashes % 2 === 0) return true;
  }
  return false;
}

function endsWithUnescapedPipe(line: string): boolean {
  if (!line.endsWith("|")) return false;
  let precedingBackslashes = 0;
  for (
    let index = line.length - 2;
    index >= 0 && line[index] === "\\";
    index -= 1
  ) {
    precedingBackslashes += 1;
  }
  return precedingBackslashes % 2 === 0;
}

function stripBlockquotePrefix(line: string): string {
  let stripped = line;
  while (true) {
    const match = /^(?: {0,3}|\t)>[ \t]?/.exec(stripped);
    if (!match) return stripped;
    stripped = stripped.slice(match[0].length);
  }
}

function isPotentialPartialDelimiter(line: string): boolean {
  const stripped = stripBlockquotePrefix(line).trim();
  return stripped.length > 0 && /^[|:\- \t]+$/.test(stripped);
}

function parseOpeningFence(line: string): T_FenceState | null {
  const stripped = stripBlockquotePrefix(line);
  const match = /^ {0,3}(`{3,}|~{3,})/.exec(stripped);
  const run = match?.[1];
  if (!run) return null;
  const marker = run[0];
  if (marker !== "`" && marker !== "~") return null;
  return { length: run.length, marker };
}

function isClosingFence(line: string, fence: T_FenceState): boolean {
  const stripped = stripBlockquotePrefix(line).replace(/^ {0,3}/, "");
  let markerCount = 0;
  while (stripped[markerCount] === fence.marker) markerCount += 1;
  return (
    markerCount >= fence.length &&
    stripped.slice(markerCount).trim().length === 0
  );
}
